import type { CharacterBuild, CharacterStyle } from './characterStyle';

/**
 * Pure skeleton definition (no three.js objects): joint hierarchy, rest offsets per build, the pose
 * buffer layout and a forward-kinematics matrix writer that instanced renderers can use directly.
 *
 * Conventions (match three.js): Y up, the character faces +Z, its left is +X. Joint rotations are
 * Euler XYZ in the parent's frame. For a limb hanging down, negative X swings it forward; for the
 * spine, positive X leans forward. Distances are metres before any visual scale.
 */

export const Joint = {
  hips: 0,
  spine: 1,
  chest: 2,
  neck: 3,
  head: 4,
  shoulderL: 5,
  upperArmL: 6,
  lowerArmL: 7,
  handL: 8,
  shoulderR: 9,
  upperArmR: 10,
  lowerArmR: 11,
  handR: 12,
  upperLegL: 13,
  lowerLegL: 14,
  footL: 15,
  upperLegR: 16,
  lowerLegR: 17,
  footR: 18,
  coatTail: 19,
} as const;
export type JointName = keyof typeof Joint;
export const JOINT_COUNT = 20;
export const JOINT_NAMES: readonly JointName[] = Object.keys(Joint) as JointName[];
/** Parent index per joint (-1 = root). Parents always precede children. */
export const JOINT_PARENTS: readonly number[] = [-1, 0, 1, 2, 3, 2, 5, 6, 7, 2, 9, 10, 11, 0, 13, 14, 0, 16, 17, 0];

/**
 * A pose is one flat Float32Array: Euler XYZ rotation per joint (3 × JOINT_COUNT), followed by the
 * hips translation offset from rest (x, y, z) — bob, crouch and falls.
 */
export type Pose = Float32Array;
export const POSE_HIPS_OFFSET = JOINT_COUNT * 3;
export const POSE_SIZE = POSE_HIPS_OFFSET + 3;

export function createPose(): Pose {
  return new Float32Array(POSE_SIZE);
}

/** Body measurements that drive both the rest skeleton and the rig's part sizes. */
export interface Proportions {
  height: number;
  /** Width / depth / limb-thickness multipliers for the build. */
  width: number;
  depth: number;
  limb: number;
  hipHeight: number;
  hipHalf: number;
  upperLeg: number;
  lowerLeg: number;
  ankle: number;
  footLength: number;
  spine: number;
  chest: number;
  neck: number;
  headRadius: number;
  shoulderHalf: number;
  shoulderHeight: number;
  upperArm: number;
  lowerArm: number;
  handSize: number;
  waistRadius: number;
  chestRadius: number;
}

export interface Skeleton {
  readonly build: CharacterBuild;
  readonly height: number;
  readonly dims: Readonly<Proportions>;
  /** Rest offsets (x, y, z) of each joint relative to its parent. */
  readonly rest: Float32Array;
}

const BUILD_FACTORS: Record<CharacterBuild, { width: number; depth: number; limb: number; belly: number }> = {
  slim: { width: 0.9, depth: 0.9, limb: 0.9, belly: 0.92 },
  average: { width: 1, depth: 1, limb: 1, belly: 1 },
  stocky: { width: 1.1, depth: 1.08, limb: 1.1, belly: 1.06 },
  heavy: { width: 1.16, depth: 1.25, limb: 1.15, belly: 1.3 },
};

/**
 * Proportions are pushed for the high top-down camera: broad shoulders, a slightly large head and
 * big hands, so the silhouette and gestures read at a glance.
 */
export function proportionsFor(build: CharacterBuild, height = 1, sculpt?: CharacterStyle['sculpt']): Proportions {
  const f = BUILD_FACTORS[build];
  const h = height;
  const ankle = 0.09 * h;
  const lowerLeg = 0.43 * h;
  const upperLeg = 0.42 * h;
  const base: Proportions = {
    height: h,
    width: f.width,
    depth: f.depth,
    limb: f.limb,
    hipHeight: ankle + lowerLeg + upperLeg,
    hipHalf: 0.1 * f.width,
    upperLeg,
    lowerLeg,
    ankle,
    footLength: 0.27 * h,
    spine: 0.1 * h,
    chest: 0.16 * h,
    neck: 0.36 * h,
    headRadius: 0.13 * (0.6 + 0.4 * h),
    shoulderHalf: 0.2 * f.width,
    shoulderHeight: 0.27 * h,
    upperArm: 0.29 * h,
    lowerArm: 0.26 * h,
    handSize: 0.058 * f.limb,
    waistRadius: 0.16 * f.width * f.belly,
    chestRadius: 0.25 * f.width,
  };
  return sculpt === 'villain' ? villainProportions(base, f.width) : base;
}

/**
 * The authored player's landmarks, measured against a reference humanoid avatar (a Mixamo-style
 * rig from hmthanh/3d-human-model, which has no licence: only these ratios were taken, no data):
 * hip joint at ~0.53 of stature (the crowd figure: 0.50), shoulder joint at ~0.80 (0.77), wrist
 * at ~0.51 (0.48) and a shoulder breadth of ~0.33 stature instead of ~0.37. Legs get longer, the
 * torso shorter and the shoulders higher and narrower, so the figure stops reading as a mannequin
 * with dangling arms. Total height is unchanged; pedestrians and police never get this.
 */
function villainProportions(d: Proportions, width: number): Proportions {
  const h = d.height;
  const upperLeg = 0.445 * h;
  const lowerLeg = 0.455 * h;
  return {
    ...d,
    upperLeg,
    lowerLeg,
    hipHeight: d.ankle + lowerLeg + upperLeg,
    spine: 0.095 * h,
    chest: 0.145 * h,
    neck: 0.335 * h,
    shoulderHalf: 0.165 * width,
    shoulderHeight: 0.3 * h,
    upperArm: 0.3 * h,
    lowerArm: 0.27 * h,
  };
}

export function createSkeleton(build: CharacterBuild = 'average', height = 1, sculpt?: CharacterStyle['sculpt']): Skeleton {
  const d = proportionsFor(build, height, sculpt);
  const rest = new Float32Array(JOINT_COUNT * 3);
  const set = (j: number, x: number, y: number, z: number): void => {
    rest[j * 3] = x;
    rest[j * 3 + 1] = y;
    rest[j * 3 + 2] = z;
  };
  set(Joint.hips, 0, d.hipHeight, 0);
  set(Joint.spine, 0, d.spine, 0);
  set(Joint.chest, 0, d.chest, 0);
  set(Joint.neck, 0, d.neck, 0);
  set(Joint.head, 0, 0.07 * height, 0.01);
  for (const [sh, ua, la, ha, side] of [
    [Joint.shoulderL, Joint.upperArmL, Joint.lowerArmL, Joint.handL, 1],
    [Joint.shoulderR, Joint.upperArmR, Joint.lowerArmR, Joint.handR, -1],
  ] as const) {
    set(sh, side * d.shoulderHalf, d.shoulderHeight, -0.01);
    set(ua, side * 0.055 * d.width, -0.02, 0);
    set(la, 0, -d.upperArm, 0);
    set(ha, 0, -d.lowerArm, 0);
  }
  for (const [ul, ll, ft, side] of [
    [Joint.upperLegL, Joint.lowerLegL, Joint.footL, 1],
    [Joint.upperLegR, Joint.lowerLegR, Joint.footR, -1],
  ] as const) {
    set(ul, side * d.hipHalf, -0.02, 0);
    set(ll, 0, -d.upperLeg, 0);
    set(ft, 0, -d.lowerLeg + 0.02, 0);
  }
  set(Joint.coatTail, 0, 0.08 * height, -0.01);
  return { build, height, dims: d, rest };
}

/** Optional root placement for writeJointMatrices: translation, yaw about +Y and uniform scale. */
export interface RootTransform {
  x: number;
  y: number;
  z: number;
  yaw: number;
  scale: number;
}

const local = new Float64Array(16);
const world = new Float64Array(16 * JOINT_COUNT);

/**
 * Forward kinematics: write one column-major 4×4 matrix per joint (JOINT_COUNT × 16 floats) into
 * `out` starting at matrix slot `slot` (i.e. float offset slot × 16). Without `root` the matrices are
 * in character space (feet at the origin); with `root` they are world matrices ready for an
 * InstancedMesh whose part geometry is authored in joint-local space. Allocates nothing.
 */
export function writeJointMatrices(skeleton: Skeleton, pose: Pose, out: Float32Array, slot = 0, root?: RootTransform): void {
  const rest = skeleton.rest;
  for (let j = 0; j < JOINT_COUNT; j++) {
    const r = j * 3;
    let tx = rest[r]!;
    let ty = rest[r + 1]!;
    let tz = rest[r + 2]!;
    if (j === Joint.hips) {
      tx += pose[POSE_HIPS_OFFSET]!;
      ty += pose[POSE_HIPS_OFFSET + 1]!;
      tz += pose[POSE_HIPS_OFFSET + 2]!;
    }
    composeLocal(pose[r]!, pose[r + 1]!, pose[r + 2]!, tx, ty, tz);
    const parent = JOINT_PARENTS[j]!;
    const w = j * 16;
    if (parent < 0) {
      if (root) {
        rootMul(root);
      } else {
        for (let k = 0; k < 16; k++) world[w + k] = local[k]!;
      }
    } else {
      mulInto(world, parent * 16, w);
    }
    const o = (slot + j) * 16;
    for (let k = 0; k < 16; k++) out[o + k] = world[w + k]!;
  }
}

/** local = T(tx,ty,tz) · R_xyz(x,y,z), three.js Euler 'XYZ' order. */
function composeLocal(x: number, y: number, z: number, tx: number, ty: number, tz: number): void {
  const a = Math.cos(x);
  const b = Math.sin(x);
  const c = Math.cos(y);
  const d = Math.sin(y);
  const e = Math.cos(z);
  const f = Math.sin(z);
  const ae = a * e;
  const af = a * f;
  const be = b * e;
  const bf = b * f;
  local[0] = c * e;
  local[1] = af + be * d;
  local[2] = bf - ae * d;
  local[3] = 0;
  local[4] = -c * f;
  local[5] = ae - bf * d;
  local[6] = be + af * d;
  local[7] = 0;
  local[8] = d;
  local[9] = -b * c;
  local[10] = a * c;
  local[11] = 0;
  local[12] = tx;
  local[13] = ty;
  local[14] = tz;
  local[15] = 1;
}

/** world[w] = world[p] · local. */
function mulInto(m: Float64Array, p: number, w: number): void {
  for (let col = 0; col < 4; col++) {
    const l0 = local[col * 4]!;
    const l1 = local[col * 4 + 1]!;
    const l2 = local[col * 4 + 2]!;
    const l3 = local[col * 4 + 3]!;
    for (let row = 0; row < 4; row++) {
      m[w + col * 4 + row] = m[p + row]! * l0 + m[p + 4 + row]! * l1 + m[p + 8 + row]! * l2 + m[p + 12 + row]! * l3;
    }
  }
}

/** world[0] = T(root) · Ry(yaw) · S(scale) · local. */
function rootMul(root: RootTransform): void {
  const c = Math.cos(root.yaw) * root.scale;
  const s = Math.sin(root.yaw) * root.scale;
  const k = root.scale;
  for (let col = 0; col < 4; col++) {
    const lx = local[col * 4]!;
    const ly = local[col * 4 + 1]!;
    const lz = local[col * 4 + 2]!;
    const lw = local[col * 4 + 3]!;
    world[col * 4] = c * lx + s * lz + root.x * lw;
    world[col * 4 + 1] = k * ly + root.y * lw;
    world[col * 4 + 2] = -s * lx + c * lz + root.z * lw;
    world[col * 4 + 3] = lw;
  }
}
